import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../context/AuthContext';
import styles from './TeamMemberEntry.module.scss';
import Avatar from '../Avatar/Avatar';
import { getAvatarSrc } from '../../utils/avatarUtils';

function getStatusColor(status) {
  switch (status) {
    case 'focus':
      return styles.statusFocus;
    case 'watched':
      return styles.statusWatched;
    default:
      return styles.statusDefault;
  }
}

// Read-only version of the component
function ReadOnlyMemberEntry({ member, status, isAdmin }) {
  const { user } = useAuth();
  
  return (
    <div className={`${styles.memberEntry} ${isAdmin ? styles.admin : ''} ${getStatusColor(status)}`}>
      <Avatar
        src={getAvatarSrc(member, user)}
        name={member.name}
        size="xsmall"
        className={styles.avatar}
      />
      <span>{member.name}</span>
    </div>
  );
}

// Interactive version with sortable functionality
function InteractiveMemberEntry({ member, skillId, levelId, status, onContextMenu, isAdmin }) {
  const { user } = useAuth();
  const memberId = String(member.id);
  const id = `${memberId}-${skillId}-${levelId || 'unassigned'}`;
  
  // Check if this is the user's own entry
  const isUserEntry = member.email === user?.email;
  const canInteract = isAdmin || isUserEntry;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled: !canInteract 
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onContextMenu={(e) => onContextMenu(e, memberId, skillId)}
      className={`${styles.memberEntry} ${canInteract ? styles.admin : ''} ${getStatusColor(status)} ${isDragging ? styles.dragging : ''}`}
    >
      <Avatar
        src={getAvatarSrc(member, user)}
        name={member.name}
        size="xsmall"
        className={styles.avatar}
      />
      <span className={styles.name}>{member.name}</span>
      {status && (
        <span className={styles.statusIndicator}>
          {status === 'focus' ? '🌱' : status === 'watched' ? '👁️' : ''}
        </span>
      )}
    </div>
  );
}

// Main component that decides which version to render
function TeamMemberEntry({ readOnly = false, ...props }) {
  if (readOnly) {
    return <ReadOnlyMemberEntry {...props} />;
  }
  
  return <InteractiveMemberEntry {...props} />;
}

export default TeamMemberEntry; 